import React from 'react';

/**
 * Full-width stacked bar with labeled segments. Used for risk distribution, etc.
 *
 * Props:
 *   segments: [{ key, label, count, color?, fg? }]
 *   showLegend: boolean
 *   total: number — auto-computed if omitted
 */
export default function SegmentedBar({
  segments = [],
  showLegend = true,
  total,
  height = 10,
  className = '',
}) {
  const sum = total ?? segments.reduce((a, s) => a + (Number(s.count) || 0), 0);
  const safeSum = sum > 0 ? sum : 1;

  return (
    <div className={className}>
      <div
        className="flex w-full overflow-hidden rounded-full"
        style={{ height, background: 'var(--bg-s1)' }}
      >
        {segments.map((seg) => {
          const pct = ((Number(seg.count) || 0) / safeSum) * 100;
          if (pct <= 0) return null;
          return (
            <div
              key={seg.key}
              style={{
                width: `${pct}%`,
                background: seg.color || 'var(--text-muted)',
                transition: 'width 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)',
              }}
              title={`${seg.label}: ${seg.count} (${pct.toFixed(1)}%)`}
            />
          );
        })}
      </div>
      {showLegend && (
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
          {segments.map((seg) => {
            const pct = ((Number(seg.count) || 0) / safeSum) * 100;
            return (
              <div key={seg.key} className="flex items-center gap-2">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: seg.color || 'var(--text-muted)' }}
                />
                <span
                  className="text-[11.5px] font-medium"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {seg.label}
                </span>
                <span
                  className="text-[11.5px] app-numeric"
                  style={{ color: 'var(--text-muted)' }}
                >
                  {seg.count} · {pct.toFixed(0)}%
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
